// =============================================================================
// siteMatch — "is this site already in the register?" (mig 451, 26 Oct 2026)
// =============================================================================
// One place for the duplicate check so the quick-add site modal (SitePicker)
// and the RFQ intake screen behave the same way. It calls
// app.site_match_candidates, which PROPOSES: 1.0 = an exact RFNSA / carrier
// site id match, 0.9 = the same name once EXCHANGE / RT / CMTS are stripped,
// below that a trigram "looks like". James: "never auto-links below exact; it
// only proposes" — so this file shows candidates and lets a person pick.
// =============================================================================

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Spinner } from "@/components/ui";

export interface SiteMatchCandidate {
  site_id: string;
  site_name: string;
  site_code: string | null;
  suburb: string | null;
  state: string | null;
  rfnsa_id: string | null;
  carrier_site_id: string | null;
  is_active: boolean;
  reason: string;
  confidence: number;
}

export interface SiteMatchInput {
  name: string;
  carrierSiteId?: string | null;
  rfnsaId?: string | null;
  suburb?: string | null;
  state?: string | null;
}

/** Call the matcher once. Returns [] when there is nothing worth asking about. */
export async function fetchSiteMatches(input: SiteMatchInput): Promise<SiteMatchCandidate[]> {
  const name = (input.name ?? "").trim();
  if (!name && !input.carrierSiteId?.trim() && !input.rfnsaId?.trim()) return [];
  const { data, error } = await (supabase as any).schema("app").rpc("site_match_candidates", {
    p_name: name || null,
    p_carrier_site_id: input.carrierSiteId?.trim() || null,
    p_rfnsa_id: input.rfnsaId?.trim() || null,
    p_suburb: input.suburb?.trim() || null,
    p_state: input.state?.trim() || null,
  });
  if (error) throw error;
  return ((data ?? []) as any[]).map(r => ({ ...r, confidence: Number(r.confidence) })) as SiteMatchCandidate[];
}

/** React-query wrapper — `enabled` lets the caller wait for a blur / a button. */
export function useSiteMatches(input: SiteMatchInput, enabled: boolean) {
  const key = [input.name, input.carrierSiteId ?? "", input.rfnsaId ?? "", input.suburb ?? "", input.state ?? ""].join("|");
  return useQuery({
    queryKey: ["site_match", key],
    enabled: enabled && key.replace(/\|/g, "").trim().length > 0,
    queryFn: () => fetchSiteMatches(input),
    staleTime: 60_000,
  });
}

export function confidenceLabel(c: number): { text: string; cls: string } {
  if (c >= 1)   return { text: "Exact match",   cls: "bg-danger/15 text-danger" };
  if (c >= 0.9) return { text: "Same name",     cls: "bg-warn/15 text-warn" };
  return        { text: `${Math.round(c * 100)}% alike`, cls: "bg-info/12 text-info" };
}

// -----------------------------------------------------------------------------
// SiteMatchList — the candidates, each with "Use this site". The caller decides
// what "create anyway" means (it differs between the modal and the intake).
// -----------------------------------------------------------------------------
export function SiteMatchList({
  candidates, loading, onUse, chosenSiteId, compact = false,
}: {
  candidates: SiteMatchCandidate[];
  loading?: boolean;
  onUse: (c: SiteMatchCandidate) => void;
  chosenSiteId?: string | null;
  compact?: boolean;
}) {
  if (loading) return <div className="py-2 flex items-center gap-2 text-[12px] text-text-muted"><Spinner size={12} /> Checking the site register…</div>;
  if (!candidates.length) return null;
  const exact = candidates.some(c => c.confidence >= 1);
  return (
    <div className={`rounded-md border ${exact ? "border-danger/40 bg-danger/5" : "border-warn/40 bg-warn/5"} ${compact ? "p-2" : "p-3"}`}>
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle size={14} className={`mt-0.5 flex-shrink-0 ${exact ? "text-danger" : "text-warn"}`} />
        <div className="text-[12px] text-text">
          {exact
            ? "This site is already in the register — the id matches."
            : `${candidates.length === 1 ? "A site that looks like this one is" : `${candidates.length} sites that look like this one are`} already in the register.`}
          <span className="text-text-muted"> Use it, or create a new one anyway.</span>
        </div>
      </div>
      <div className="divide-y divide-line">
        {candidates.map(c => {
          const lab = confidenceLabel(c.confidence);
          const chosen = chosenSiteId === c.site_id;
          return (
            <div key={c.site_id} className="py-1.5 flex items-center gap-2">
              <span className={`text-[10px] font-mono uppercase tracking-wider2 px-1.5 py-0.5 rounded whitespace-nowrap ${lab.cls}`}>{lab.text}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-text truncate">
                  <span className="font-mono text-accent mr-1.5">{c.site_code ?? "—"}</span>
                  {c.site_name}
                  {!c.is_active && <span className="ml-1.5 text-[10px] text-text-muted">inactive</span>}
                  <Link to={`/sites/${c.site_id}`} target="_blank" rel="noopener noreferrer" className="ml-1.5 text-text-muted hover:text-accent inline-flex align-middle" title="Open the site page in a new tab">
                    <ExternalLink size={10} />
                  </Link>
                </div>
                <div className="text-[11px] text-text-muted truncate">
                  {[c.suburb, c.state].filter(Boolean).join(", ")}
                  {c.rfnsa_id ? ` · RFNSA ${c.rfnsa_id}` : ""}
                  {c.carrier_site_id ? ` · ${c.carrier_site_id}` : ""}
                  {" · "}{c.reason}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onUse(c)}
                className={`text-[11px] px-2 py-1 rounded border whitespace-nowrap transition-colors ${chosen ? "border-ok/60 text-ok bg-ok/10" : "border-line text-text-muted hover:text-accent hover:border-accent/50"}`}
              >
                {chosen ? "Using this site" : "Use this site"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
